<template>
    <div class="load-cost-chart-container">
        <div class="stack-chart-wrapper">
            <!-- 스택바 -->
            <div class="stack-bar" ref="stackBar">
                <div
                    v-for="(item, index) in processedData"
                    :key="`segment-${index}`"
                    class="stack-segment"
                    :style="getSegmentStyle(item)"
                    role="button"
                    :tabindex="0"
                >
                    <div class="segment-content">
                        <span class="segment-label"
                            >{{ item.label }}<br />
                            <span class="segment-label-percentage">({{ item.percentage.toFixed(0) }}%)</span></span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "BillLoadCostChart",
    props: {
        data: {
            type: Array,
            default: () => [],
            validator: (value) => Array.isArray(value),
        },
        activeTab: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            tooltipStyle: {},
            processedData: [],
        };
    },

    computed: {
        /**
         * 총 금액 계산
         */
        totalAmount() {
            return this.data.reduce((sum, item) => sum + (item.cost || 0), 0);
        },

        /**
         * 부하구간 라벨 매핑
         */
        loadSectionLabels() {
            return {
                경부하: "경부하",
                중부하: "중부하",
                최대부하: "최대부하",
            };
        },
    },

    watch: {
        data: {
            handler() {
                this.processData();
            },
            immediate: true,
            deep: true,
        },
    },

    mounted() {
        this.processData();
    },

    beforeDestroy() {},

    methods: {
        /**
         * 데이터 처리
         */
        processData() {
            if (!this.data || this.data.length === 0) {
                this.processedData = [];
                return;
            }

            let cumulativePercentage = 0;
            const totalItems = this.data.length;

            this.processedData = this.data.map((item, index) => {
                const cost = item.cost || 0;
                const percentage = this.totalAmount > 0 ? (cost / this.totalAmount) * 100 : 0;
                const startPercentage = cumulativePercentage;

                // 마지막 아이템인 경우 100%에 맞추기
                let adjustedPercentage = percentage;
                if (index === totalItems - 1) {
                    adjustedPercentage = Math.max(0, 100 - startPercentage);
                }

                cumulativePercentage += adjustedPercentage;

                // 100%를 넘지 않도록 제한
                cumulativePercentage = Math.min(cumulativePercentage, 100);

                const processedItem = {
                    name: item.loadSection,
                    label: this.loadSectionLabels[item.loadSection] || item.loadSection,
                    value: cost,
                    percentage: adjustedPercentage,
                    startPercentage: startPercentage,
                    endPercentage: cumulativePercentage,
                    color: this.getColor(index),
                    originalData: item,
                };

                return processedItem;
            });

            const totalPercentage = this.processedData.reduce((sum, item) => sum + item.percentage, 0);

            // 100% 초과 경고
            if (totalPercentage > 100.01) {
                // 부동소수점 오차 고려
                console.warn("⚠️ 총 비율이 100%를 초과합니다:", totalPercentage.toFixed(2) + "%");
            }

            // 마지막 구간이 100%를 넘는지 확인
            const lastItem = this.processedData[this.processedData.length - 1];
            if (lastItem && lastItem.endPercentage > 100) {
                console.warn(
                    "⚠️ 마지막 구간이 100%를 넘습니다:",
                    lastItem.label,
                    lastItem.endPercentage.toFixed(2) + "%"
                );
            }
        },

        /**
         * 색상 가져오기 - 에너지 친화적인 색상 팔레트
         */
        getColor(index) {
            const colors = [
                "#22c55e", // 경부하:  녹색
                "#f59e0b", // 중부하: 주황색
                "#dc2626", // 최대부하: 경고 빨간색
            ];
            return colors[index % colors.length];
        },

        /**
         * 세그먼트 스타일 생성
         */
        getSegmentStyle(item) {
            // 100%를 넘지 않도록 보장
            const maxEndPercentage = Math.min(item.endPercentage, 100);
            const maxStartPercentage = Math.min(item.startPercentage, 100);

            // 실제 너비 계산 (100%를 넘지 않도록)
            let actualWidth = Math.max(0, maxEndPercentage - maxStartPercentage);

            // 최소 너비 보장 (매우 작은 비율의 경우에도 보이도록)
            if (actualWidth < 1 && item.percentage > 0) {
                actualWidth = 1;
            }

            return {
                left: `${maxStartPercentage}%`,
                width: `${actualWidth}%`,
                backgroundColor: item.color,
                minWidth: "2px", // 최소 2px 너비 보장
                maxWidth: "100%", // 최대 너비 제한
            };
        },
    },
};
</script>

<style scoped>
.segment-label-percentage {
    font-size: 10px;
    font-weight: bold;
    color: #fff;
    margin-left: 4px;
}

.load-cost-chart-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent;
}

.stack-chart-wrapper {
    position: relative;
    width: 100%;
    min-height: 40px;
    background: transparent;
    margin-bottom: 10px;
}

.stack-bar {
    position: relative;
    width: 100%;
    height: 40px;
    display: flex;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background: #f5f5f5;
}

.stack-segment {
    position: absolute;
    /* 절대 위치로 변경하여 overflow 방지 */
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    border-right: 1px solid rgba(255, 255, 255, 0.3);
    min-width: 2px;
    /* 최소 너비 보장 */
    max-width: 100%;
    /* 최대 너비 제한 */
    overflow: hidden;
    /* 내용이 넘치지 않도록 */
    box-sizing: border-box;
    height: 100%;
    /* 부모 높이에 맞춤 */
}

.stack-segment:last-child {
    border-right: none;
}

.stack-segment:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
}

.segment-content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0 4px;
}

.segment-label {
    color: white;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    white-space: nowrap;
    overflow: visible;
    /* 작은 구간에서도 라벨이 보이도록 */
    text-overflow: ellipsis;
    pointer-events: none;
    /* 라벨 클릭 방지 */
}

/* 매우 작은 구간의 경우 라벨 숨김 */
.stack-segment[style*="width: 1%"] .segment-label,
.stack-segment[style*="width: 2%"] .segment-label {
    display: none;
}
</style>
